// packages/app/src/components/ui/DateRangePicker.tsx
import {
  DateRangePicker as AriaDateRangePicker,
  Group,
  DateInput,
  DateSegment,
  Button as AriaButton,
  Popover,
  Dialog,
  RangeCalendar,
  Heading,
  CalendarGrid,
  CalendarGridHeader,
  CalendarHeaderCell,
  CalendarGridBody,
  CalendarCell,
} from 'react-aria-components';
import {
  CalendarDays,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  X,
} from 'lucide-react';
import { parseDate } from '@internationalized/date';
import type { CalendarDate } from '@internationalized/date';
import { cx } from './cx';
import { Icon } from './Icon';
import { typography } from './Text';
import { useT } from '../../modules/i18n';

export type DateRangeValue = { start: string; end: string } | null;

export interface DateRangePickerProps {
  value: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
  label?: string;
  className?: string;
}

/**
 * Design-system date-range picker built on react-aria-components +
 * @internationalized/date. Exposes plain `YYYY-MM-DD` strings to callers; all
 * color/spacing comes from foundation tokens.
 */
export const DateRangePicker = ({
  value,
  onChange,
  label,
  className,
}: DateRangePickerProps) => {
  const t = useT();
  const ariaValue =
    value && value.start && value.end
      ? { start: parseDate(value.start), end: parseDate(value.end) }
      : null;

  return (
    <AriaDateRangePicker
      aria-label={label ?? t('ui.dateRangePicker.ariaLabel')}
      value={ariaValue}
      onChange={range =>
        onChange(
          range
            ? { start: range.start.toString(), end: range.end.toString() }
            : null,
        )
      }
      className={cx('flex flex-col gap-1.5', className)}
    >
      {label && (
        <span className={cx(typography.label, 'text-fg-secondary')}>
          {label}
        </span>
      )}
      <Group className="flex min-h-10 items-center gap-2 rounded-lg border border-strong bg-surface px-2.5 py-1.5 shadow-xs outline-none transition focus-within:border-brand-300 focus-within:ring-4 focus-within:ring-[rgb(var(--c-ring)/0.24)]">
        <Icon as={CalendarDays} size="sm" className="shrink-0 text-fg-muted" />
        <DateInput slot="start" className="flex">
          {segment => (
            <DateSegment
              segment={segment}
              className="rounded px-1 text-sm text-fg-secondary tabular-nums outline-none data-[focused]:bg-brand-solid data-[focused]:text-base-white data-[placeholder]:text-fg-muted"
            />
          )}
        </DateInput>
        <span aria-hidden="true" className="text-fg-muted">
          –
        </span>
        <DateInput slot="end" className="flex">
          {segment => (
            <DateSegment
              segment={segment}
              className="rounded px-1 text-sm text-fg-secondary tabular-nums outline-none data-[focused]:bg-brand-solid data-[focused]:text-base-white data-[placeholder]:text-fg-muted"
            />
          )}
        </DateInput>
        <div className="ml-auto flex items-center gap-0.5">
          {ariaValue && (
            <AriaButton
              // Not a react-aria form-reset slot — the picker is controlled, so
              // clearing is just `onChange(null)`. Rendered only when a range is
              // set; `preventFocusOnPress` keeps the popover from opening.
              slot={null}
              aria-label={t('ui.dateRangePicker.clear')}
              preventFocusOnPress
              onPress={() => onChange(null)}
              className="rounded bg-transparent p-0.5 text-fg-muted outline-none hover:text-fg-secondary"
            >
              <Icon as={X} size="sm" />
            </AriaButton>
          )}
          <AriaButton className="rounded bg-transparent p-0.5 text-fg-muted outline-none hover:text-fg-secondary">
            <Icon as={ChevronDown} size="sm" />
          </AriaButton>
        </div>
      </Group>
      <Popover className="rounded-lg border border-default bg-surface p-3 shadow-lg outline-none entering:animate-in entering:fade-in-0 entering:zoom-in-95">
        <Dialog className="outline-none">
          <RangeCalendar className="w-fit">
            <header className="mb-2 flex items-center justify-between gap-2">
              <AriaButton
                slot="previous"
                className="rounded bg-transparent p-1 text-fg-tertiary outline-none hover:bg-muted hover:text-fg-secondary"
              >
                <Icon as={ChevronLeft} size="sm" />
              </AriaButton>
              <Heading className={cx(typography.label, 'text-fg')} />
              <AriaButton
                slot="next"
                className="rounded bg-transparent p-1 text-fg-tertiary outline-none hover:bg-muted hover:text-fg-secondary"
              >
                <Icon as={ChevronRight} size="sm" />
              </AriaButton>
            </header>
            <CalendarGrid className="border-collapse">
              <CalendarGridHeader>
                {day => (
                  <CalendarHeaderCell className="pb-1.5 text-xs font-medium text-fg-tertiary">
                    {day}
                  </CalendarHeaderCell>
                )}
              </CalendarGridHeader>
              <CalendarGridBody>
                {(date: CalendarDate) => (
                  <CalendarCell
                    date={date}
                    className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-sm text-fg-secondary outline-none data-[disabled]:opacity-40 data-[hovered]:bg-muted-strong data-[outside-month]:text-fg-muted data-[selected]:bg-muted-strong data-[selected]:text-fg data-[selection-end]:bg-brand-solid data-[selection-end]:text-base-white data-[selection-start]:bg-brand-solid data-[selection-start]:text-base-white"
                  />
                )}
              </CalendarGridBody>
            </CalendarGrid>
          </RangeCalendar>
        </Dialog>
      </Popover>
    </AriaDateRangePicker>
  );
};
